import type { ApprovalAttributes } from '@/types/approval';

/** SAP 담보 신규(154)·변경(155) — 레거시 appr_edit_forms_new_shop_lending·update_shop_lending. SAP `Ep` 진입 전용 */
export const SAP_COLLATERAL_CREATION_TYPE = 154;
export const SAP_COLLATERAL_CHANGE_TYPE = 155;

export const isSapCollateralChangeType = (apvType: number): boolean =>
  apvType === SAP_COLLATERAL_CHANGE_TYPE;

export const isSapCollateralType = (apvType: number): boolean =>
  apvType === SAP_COLLATERAL_CREATION_TYPE ||
  apvType === SAP_COLLATERAL_CHANGE_TYPE;

/** 레거시 approval_interface 가 SAP Gwdocno 를 `Ep` 로 넘김 */
export const SAP_COLLATERAL_ENTRY_QUERY_KEY = 'Ep';

export const SAP_COLLATERAL_MISSING_MESSAGE = '데이터를 불러오지 못했습니다.';
export const SAP_COLLATERAL_LOADING_MESSAGE =
  '데이터를 불러오는 중입니다. 잠시 후 다시 시도해주세요.';

export type SapCollateralEstate = {
  ESTATETYPE?: string;
  ESTATEVALUE?: string;
  SECURITYAMT?: string;
  SECURITYRANKING?: string;
};

export type SapCollateralSurety = {
  SURETY_AMT?: string;
  SURETY_TODATE?: string;
};

export type SapCollateralOther = {
  OTHERSECURITY?: string;
  OTHERSECURITYAMT?: string;
  OTHERSECURITYDATE?: string;
  FROMAVAILABLEDATE?: string;
  TOAVAILABLEDATE?: string;
};

/** SAP 매장 여신 응답 한 건 — 숫자도 콤마 포함 문자열 */
export type SapCollateralRecord = {
  HEAD?: {
    KATR2?: string;
    ALTKN?: string;
    ALTKN_T?: string;
    KUNNR?: string;
    ZTITLE?: string;
  };
  HEAD2?: {
    COMMENTS?: string;
    CREDIT_AMT?: string;
    TOT_COLL_AMT?: string;
    USEYN?: string;
  };
  ESTATE?: SapCollateralEstate[];
  SURETY?: SapCollateralSurety[];
  OTHER?: SapCollateralOther[];
};

export type SapCollateralDraftSource = {
  code: number;
  attributes?: { sapKey?: string };
  /** 155 는 source[0]=변경 전·source[1]=변경 후, 154 는 source[0]=신규 */
  display?: { source?: SapCollateralRecord[] | null };
};

export type SapCollateralChangeState = {
  sapKey: string | null;
  /** 155 변경 전. 154 는 늘 null */
  before: SapCollateralRecord | null;
  /** 155 변경 후, 154 신규 — 제목(ZTITLE)도 여기서 */
  after: SapCollateralRecord | null;
  loading: boolean;
  /** 조회로 채운 제목 — 이탈 경고 판정에서 사용자 입력으로 보지 않음 */
  autoSubject?: string;
};

export const createSapCollateralChangeState = (
  sapKey: string | null = null,
): SapCollateralChangeState => ({
  sapKey,
  before: null,
  after: null,
  loading: false,
});

/** 155 는 변경 전·후, 154 는 첫 건이 있어야 표를 그림 — 없으면 레거시도 표·제목을 못 그림 */
export const pickSapCollateralRecords = (
  apvType: number,
  source: SapCollateralRecord[] | null | undefined,
): {
  before: SapCollateralRecord | null;
  after: SapCollateralRecord;
} | null => {
  if (!Array.isArray(source)) return null;
  if (!isSapCollateralChangeType(apvType)) {
    return source[0] ? { before: null, after: source[0] } : null;
  }
  const [before, after] = source;
  if (!before || !after) return null;
  return { before, after };
};

export const getSapCollateralEntrySapKey = (
  query: Record<string, string | string[] | undefined>,
): string | undefined => {
  const raw = query[SAP_COLLATERAL_ENTRY_QUERY_KEY];
  const value = (Array.isArray(raw) ? raw[0] : raw)?.trim();
  return value || undefined;
};

export const parseSapCollateralSapKey = (
  attributes: Record<string, unknown> | null | undefined,
): string | null => {
  const value = attributes?.sapKey;
  return typeof value === 'string' && value.trim() ? value.trim() : null;
};

export const parseSapCollateralContent = (
  attributes: Record<string, unknown> | null | undefined,
): string | null => {
  const content = attributes?.content;
  return typeof content === 'string' && content.trim() ? content : null;
};

/** 임시저장 버튼 없음. 상신은 sapKey 없음(서버 422)·조회 중만 막고, 조회 실패는 빈 표로 상신(레거시 동일) */
export const checkSapCollateralChangeBeforeSave = (
  state: SapCollateralChangeState | null | undefined,
): string | null => {
  if (!state?.sapKey) return SAP_COLLATERAL_MISSING_MESSAGE;
  if (state.loading) return SAP_COLLATERAL_LOADING_MESSAGE;
  return null;
};

export const buildSapCollateralChangeAttributes = (
  state: SapCollateralChangeState | null | undefined,
  content: string,
): ApprovalAttributes | null =>
  state?.sapKey ? { sapKey: state.sapKey, content } : null;
